

// Helper to convert image URL to Base64
const getBase64ImageFromUrl = async (imageUrl) => {
    try {
        const res = await fetch(imageUrl);
        const blob = await res.blob();
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    } catch (e) {
        console.warn('Failed to load image for PDF/HTML:', imageUrl);
        return null;
    }
};

export const generateQuotationHTML = (quotation, logoBase64) => {
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Safe checks for fields
    const q = {
        ...quotation,
        items: quotation.items || [],
        customer_address: quotation.customer_address || '-',
        customer_gstin: quotation.customer_gstin || '-',
        architect_name: quotation.architect_name || '-',
        customer_phone: quotation.customer_phone || '-',
        project: quotation.project || '-',
        prepared_by: quotation.prepared_by || '-',
        confirmed_by: quotation.confirmed_by || '-',
        sales: quotation.sales || '-',
    };

    const logoHtml = logoBase64
        ? `<img src="${logoBase64}" alt="Venzo Wood" style="width: 80px; height: auto; margin-right: 20px;">`
        : '';

    const htmlContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Quotation - ${q.quotation_number}</title>
    <style>
        * {
            box-sizing: border-box;
        }
        body {
            font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #f5f5f5;
            color: #333;
            -webkit-print-color-adjust: exact;
        }
        .container {
            width: 100%;
            max-width: 800px;
            margin: 0 auto;
            background: #fff;
            padding: 30px;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
        }
        @page {
            margin: 10mm;
            size: A4;
        }
        .header {
            display: flex;
            justify-content: flex-start;
            align-items: flex-start;
            border-bottom: 2px solid #000;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        .company-info {
            flex: 1;
        }
        .company-name {
            font-size: 24px;
            font-weight: bold;
            letter-spacing: 1px;
            margin-bottom: 5px;
            text-transform: uppercase;
        }
        .company-address {
            font-size: 14px;
            line-height: 1.4;
            color: #555;
        }
        .contact-info {
            text-align: right;
            font-weight: bold;
            font-size: 16px;
            white-space: nowrap;
            margin-left: 20px;
        }
        /* ... existing styles ... */
        .quotation-title {
            text-align: center;
            font-size: 28px;
            font-weight: bold;
            margin: 20px 0;
            text-transform: uppercase;
            letter-spacing: 2px;
            color: #000;
        }
        .meta-bar {
            display: flex;
            justify-content: space-between;
            background: #f8f8f8;
            padding: 10px 20px;
            border-radius: 4px;
            margin-bottom: 30px;
            border: 1px solid #eee;
        }
        .meta-item { font-size: 14px; }
        .meta-label { font-weight: bold; margin-right: 5px; }
        .details-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
            margin-bottom: 30px;
        }
        .details-column h3 {
            font-size: 16px;
            text-transform: uppercase;
            border-bottom: 1px solid #ddd;
            padding-bottom: 8px;
            margin-bottom: 15px;
            color: #444;
        }
        .info-row { display: flex; margin-bottom: 8px; font-size: 14px; }
        .info-label { width: 120px; font-weight: bold; color: #666; flex-shrink: 0; }
        .info-value { flex: 1; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 30px; font-size: 14px; }
        th { background-color: #000; color: #fff; text-align: left; padding: 12px; text-transform: uppercase; font-size: 12px; letter-spacing: 0.5px; }
        td { padding: 12px; border-bottom: 1px solid #eee; vertical-align: top; }
        tr:nth-child(even) { background-color: #f9f9f9; }
        .text-right { text-align: right; }
        .text-center { text-align: center; }
        .total-section { display: flex; justify-content: flex-end; margin-bottom: 40px; }
        .total-box { width: 300px; background: #f8f8f8; padding: 20px; border-radius: 4px; border: 1px solid #ddd; }
        .total-row { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 14px; }
        .grand-total { font-size: 18px; font-weight: bold; border-top: 2px solid #ddd; padding-top: 10px; margin-top: 10px; }
        .footer-grid { display: grid; grid-template-columns: 1.5fr 1fr; gap: 40px; font-size: 12px; }
        .terms ul { padding-left: 20px; margin: 0; }
        .terms li { margin-bottom: 4px; color: #555; }
        .bank-details { background: #f0f7ff; padding: 20px; border-radius: 8px; border: 1px solid #cfe2ff; }
        .bank-row { display: flex; margin-bottom: 6px; }
        .bank-label { width: 100px; font-weight: bold; color: #004085; }
        @media print {
            body { background: #fff; padding: 0; margin: 0; }
            .container { 
                box-shadow: none; 
                width: 100%; 
                max-width: 100%; 
                padding: 0; 
                margin: 0; 
                border: none;
            }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            ${logoHtml}
            <div class="company-info">
                <div class="company-name">VENZO WOOD PRIVATE LIMITED</div>
                <div class="company-address">
                    Andheri, Lower Parel, Surat, Nashik, Hyderabad<br>
                    Email: info@venzowood.com
                </div>
            </div>
            <div class="contact-info">
                +91 80802 82228
            </div>
        </div>


        <div class="quotation-title">Quotation</div>

        <div class="meta-bar">
            <div class="meta-item">
                <span class="meta-label">Date:</span>
                ${formatDate(q.created_at)}
            </div>
            <div class="meta-item">
                <span class="meta-label">Ref No:</span>
                ${q.quotation_number}
            </div>
        </div>

        <div class="details-grid">
            <div class="details-column">
                <h3>Reference Details</h3>
                <div class="info-row">
                    <span class="info-label">Sales:</span>
                    <span class="info-value">${q.sales}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Prepared by:</span>
                    <span class="info-value">${q.prepared_by}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Project:</span>
                    <span class="info-value">${q.project}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Confirmed By:</span>
                    <span class="info-value">${q.confirmed_by}</span>
                </div>
            </div>
            <div class="details-column">
                <h3>Customer Details</h3>
                <div class="info-row">
                    <span class="info-label">Client Name:</span>
                    <span class="info-value">${q.customer_name}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Email ID:</span>
                    <span class="info-value">${q.customer_email}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Address:</span>
                    <span class="info-value">${q.customer_address}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">GSTIN No:</span>
                    <span class="info-value">${q.customer_gstin}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Arch Name:</span>
                    <span class="info-value">${q.architect_name}</span>
                </div>
                <div class="info-row">
                    <span class="info-label">Tel:</span>
                    <span class="info-value">${q.customer_phone}</span>
                </div>
            </div>
        </div>

        <table>
            <thead>
                <tr>
                    <th style="width: 50px" class="text-center">#</th>
                    <th>Description</th>
                    <th style="width: 100px" class="text-center">Qty</th>
                    <th style="width: 150px" class="text-right">Price per Sqft</th>
                    <th style="width: 150px" class="text-right">Total Price</th>
                </tr>
            </thead>
            <tbody>
                ${q.items.map((item, index) => {
        // Build description details
        const details = [];
        if (item.finish) details.push(`Finish: ${item.finish}`);
        if (item.thickness) details.push(`Thickness: ${item.thickness}`);
        if (item.size) details.push(`Size: ${item.size}`);

        const descriptionHtml = details.length > 0
            ? `<div style="font-size:12px; color:#666; margin-top:2px;">${details.join(' | ')}</div>`
            : '';

        return `
                <tr>
                    <td class="text-center">${index + 1}</td>
                    <td>
                        <div style="font-weight:bold;">${item.product_name || 'Product'}</div>
                        ${descriptionHtml}
                    </td>
                    <td class="text-center">${item.quantity} Sheets</td>
                    <td class="text-right">${formatCurrency(item.unit_price)}</td>
                    <td class="text-right"><strong>${formatCurrency(item.total_price)}</strong></td>
                </tr>
                `;
    }).join('')}
            </tbody>
        </table>

        <div class="total-section">
            <div class="total-box">
                <div class="total-row grand-total">
                    <span>Grand Total</span>
                    <span>${formatCurrency(q.total_amount)}</span>
                </div>
            </div>
        </div>

        <div class="footer-grid">
            <div class="terms">
                <h3 style="margin-top:0; border-bottom:1px solid #ccc; padding-bottom:5px;">Terms & Conditions</h3>
                <ul>
                    <li>Rates are delivery ex mumbai</li>
                    <li>Payment 100% advance. Delivery in IMM after receipt of full payment for fluting.</li>
                    <li>Any changes in Government Policy will be applicable at time of delivery.</li>
                    <li>Goods once sold should not be taken back. Orders placed cannot be cancelled or exchanged.</li>
                    <li>Quality inspection at our end shall be treated as final.</li>
                    <li>Issue a Purchase Order to organise delivery of the goods.</li>
                    <li>Issue a cheque for 100% of the amount in favour of VENZO WOOD PRIVATE LIMITED.</li>
                    <li>As veneers are natural items no guarantee / warranty for colour & figure variations.</li>
                    <li>Any dispute subject to mumbai jurisdiction.</li>
                    <li>Our Delivery Ex-warehouse</li>
                    <li>Kindly note our GST No. 27AAHCV3921F1ZC</li>
                </ul>
            </div>
            
            <div class="bank-details">
                <h3 style="margin-top:0; color:#004085; border-bottom:1px solid #b8daff; padding-bottom:5px;">Bank Details</h3>
                <div class="bank-row">
                    <span class="bank-label">Company:</span>
                    <span>VENZO WOOD PRIVATE LIMITED</span>
                </div>
                <div class="bank-row">
                    <span class="bank-label">Bank:</span>
                    <span>HDFC Bank</span>
                </div>
                <div class="bank-row">
                    <span class="bank-label">A/C No:</span>
                    <span>50200093579707</span>
                </div>
                <div class="bank-row">
                    <span class="bank-label">IFSC/IBAN:</span>
                    <span>HDFC0000668</span>
                </div>
                <div class="bank-row">
                    <span class="bank-label">Branch:</span>
                    <span>Vile Parla East</span>
                </div>
                <div class="bank-row">
                    <span class="bank-label">Currency:</span>
                    <span>INR</span>
                </div>
            </div>
        </div>
    </div>
    <script>
        // Auto print when opened (optional, user can just view/save)
        // window.print();
    </script>
</body>
</html>
  `;

    return htmlContent;
};

export const downloadQuotationHTML = async (quotation) => {
    try {
        const logoBase64 = await getBase64ImageFromUrl('/venzo-logo.png');
        const html = generateQuotationHTML(quotation, logoBase64);
        const blob = new Blob([html], { type: 'text/html' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `Quotation_${quotation.quotation_number}.html`;
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Delay revocation to avoid Safari/PWA download race conditions.
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
        }, 2000);

        return true;
    } catch (error) {
        console.error('Error downloading HTML:', error);
        throw error;
    }
};
